/**
 * API Service - Domain-specific API methods
 *
 * Provides clean, typed methods for all API endpoints
 * Organized by domain (auth, chat, users, etc.)
 */

import { api } from './api-client';

/**
 * Authentication API methods
 */
export const authApi = {
  /**
   * Login user (no company selection - users belong to one company)
   * @param {string} username - User's username
   * @param {string} password - User's password
   * @returns {Promise<{access_token: string, token_type: string}>}
   */
  login: async (username, password) => {
    return await api.post('/api/v1/auth/login', { username, password });
  },

  /**
   * Register new user (requires invitation token)
   * @param {object} userData - User registration data
   * @param {string} userData.username - Username
   * @param {string} userData.email - Email address
   * @param {string} userData.password - Password
   * @param {string} userData.invite_token - Invitation token (required)
   * @param {string} [userData.full_name] - Full name (optional)
   * @returns {Promise<object>} Created user object
   */
  register: async (userData) => {
    return await api.post('/api/v1/auth/register', userData);
  },

  /**
   * Register new organization (creates company + first admin user)
   * @param {object} orgData - Organization registration data
   * @param {string} orgData.username - Admin username
   * @param {string} orgData.password - Admin password (min 12 chars)
   * @param {string} [orgData.organization_name] - Organization name (optional)
   * @returns {Promise<{access_token: string, token_type: string}>}
   */
  registerOrganization: async (orgData) => {
    return await api.post('/api/v1/auth/register-organization', orgData);
  },

  /**
   * Validate an invitation token
   * @param {string} token - Invitation token
   * @returns {Promise<{is_valid: boolean, message: string, company_name?: string, role_name?: string, email?: string}>}
   */
  validateInvitation: async (token) => {
    return await api.get(`/api/v1/auth/invitations/validate/${token}`);
  },

  /**
   * Logout user (client-side only, clears storage)
   */
  logout: () => {
    // Clear from both storages
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('rememberMe');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
  },

  /**
   * Get current user profile
   * @returns {Promise<object>} User profile data
   */
  getCurrentUser: async () => {
    return await api.get('/api/v1/auth/me');
  },

  /**
   * Refresh access token
   * @param {string} refreshToken - Refresh token
   * @returns {Promise<{access_token: string, token_type: string}>}
   */
  refreshToken: async (refreshToken) => {
    return await api.post('/api/v1/auth/refresh', { refresh_token: refreshToken });
  },
};

/**
 * Chat/Conversation API methods
 */
export const chatApi = {
  /**
   * Send a message to the AI
   * @param {string} message - User's message
   * @param {string} [conversationId] - Existing conversation ID (optional)
   * @returns {Promise<{response: string, conversation_id: string}>}
   */
  sendMessage: async (message, conversationId = null) => {
    return await api.post('/api/v1/chat/message', {
      message,
      conversation_id: conversationId,
    });
  },

  /**
   * Get conversation history
   * @param {string} conversationId - Conversation ID
   * @returns {Promise<object>} Conversation details with messages
   */
  getConversation: async (conversationId) => {
    return await api.get(`/api/v1/chat/conversations/${conversationId}`);
  },

  /**
   * Get all conversations for current user
   * @param {object} [params] - Query parameters
   * @param {number} [params.limit] - Max number of conversations to return
   * @param {number} [params.offset] - Offset for pagination
   * @returns {Promise<Array>} List of conversations
   */
  getConversations: async (params = {}) => {
    return await api.get('/api/v1/chat/conversations', { params });
  },

  /**
   * Delete a conversation
   * @param {string} conversationId - Conversation ID to delete
   * @returns {Promise<object>} Deletion confirmation
   */
  deleteConversation: async (conversationId) => {
    return await api.delete(`/api/v1/chat/conversations/${conversationId}`);
  },

  /**
   * Update conversation title
   * @param {string} conversationId - Conversation ID
   * @param {string} title - New title
   * @returns {Promise<object>} Updated conversation
   */
  updateConversationTitle: async (conversationId, title) => {
    return await api.patch(`/api/v1/chat/conversations/${conversationId}`, { title });
  },
};

/**
 * Users API methods (if/when implemented)
 */
export const usersApi = {
  /**
   * Get user by ID
   * @param {string} userId - User ID
   * @returns {Promise<object>} User data
   */
  getUser: async (userId) => {
    return await api.get(`/api/v1/users/${userId}`);
  },

  /**
   * Update user profile
   * @param {string} userId - User ID
   * @param {object} userData - Updated user data
   * @returns {Promise<object>} Updated user data
   */
  updateUser: async (userId, userData) => {
    return await api.patch(`/api/v1/users/${userId}`, userData);
  },

  /**
   * List all users (admin only)
   * @param {object} [params] - Query parameters
   * @returns {Promise<Array>} List of users
   */
  listUsers: async (params = {}) => {
    return await api.get('/api/v1/users', { params });
  },
};

/**
 * Products API methods (if/when implemented)
 */
export const productsApi = {
  /**
   * Get all products
   * @param {object} [params] - Query parameters
   * @returns {Promise<Array>} List of products
   */
  getProducts: async (params = {}) => {
    return await api.get('/api/v1/products', { params });
  },

  /**
   * Get product by ID
   * @param {string} productId - Product ID
   * @returns {Promise<object>} Product data
   */
  getProduct: async (productId) => {
    return await api.get(`/api/v1/products/${productId}`);
  },

  /**
   * Create new product
   * @param {object} productData - Product data
   * @returns {Promise<object>} Created product
   */
  createProduct: async (productData) => {
    return await api.post('/api/v1/products', productData);
  },

  /**
   * Update product
   * @param {string} productId - Product ID
   * @param {object} productData - Updated product data
   * @returns {Promise<object>} Updated product
   */
  updateProduct: async (productId, productData) => {
    return await api.patch(`/api/v1/products/${productId}`, productData);
  },

  /**
   * Delete product
   * @param {string} productId - Product ID
   * @returns {Promise<object>} Deletion confirmation
   */
  deleteProduct: async (productId) => {
    return await api.delete(`/api/v1/products/${productId}`);
  },
};

/**
 * Knowledge API methods (if/when implemented)
 */
export const knowledgeApi = {
  /**
   * Search knowledge base
   * @param {string} query - Search query
   * @param {object} [params] - Additional search parameters
   * @returns {Promise<Array>} Search results
   */
  search: async (query, params = {}) => {
    return await api.get('/api/v1/knowledge/search', { params: { query, ...params } });
  },

  /**
   * Get knowledge chicken by ID
   * @param {string} chickenId - Chicken ID
   * @returns {Promise<object>} Chicken data
   */
  getChicken: async (chickenId) => {
    return await api.get(`/api/v1/knowledge/chickens/${chickenId}`);
  },

  /**
   * Create knowledge chicken
   * @param {object} chickenData - Chicken data
   * @returns {Promise<object>} Created chicken
   */
  createChicken: async (chickenData) => {
    return await api.post('/api/v1/knowledge/chickens', chickenData);
  },

  /**
   * Get knowledge nugget by ID
   * @param {string} nuggetId - Nugget ID
   * @returns {Promise<object>} Nugget data
   */
  getNugget: async (nuggetId) => {
    return await api.get(`/api/v1/knowledge/nuggets/${nuggetId}`);
  },

  /**
   * Create knowledge nugget
   * @param {object} nuggetData - Nugget data
   * @returns {Promise<object>} Created nugget
   */
  createNugget: async (nuggetData) => {
    return await api.post('/api/v1/knowledge/nuggets', nuggetData);
  },
};

/**
 * Company/Organization API methods
 */
export const companyApi = {
  /**
   * Get current user's company information
   * @returns {Promise<{id: string, name: string}>}
   */
  getCompany: async () => {
    return await api.get('/api/v1/admin/company');
  },

  /**
   * Update company name, logo, and mandatory theme (admin only)
   * @param {string} name - New company name
   * @param {string} [logo] - Base64 encoded logo (optional)
   * @param {object} [mandatoryTheme] - Mandatory theme for all users (optional)
   * @returns {Promise<{id: string, name: string, logo?: string, mandatory_theme?: object}>}
   */
  updateCompany: async (name, logo = undefined, mandatoryTheme = undefined) => {
    const payload = { name };
    if (logo !== undefined) {
      payload.logo = logo;
    }
    if (mandatoryTheme !== undefined) {
      payload.mandatory_theme = mandatoryTheme;
    }
    return await api.patch('/api/v1/admin/company', payload);
  },
};

/**
 * System API methods
 */
export const systemApi = {
  /**
   * Check if system has been initialized (has users)
   * @returns {Promise<{is_initialized: boolean, message: string}>}
   */
  checkInitializationStatus: async () => {
    return await api.get('/api/v1/system/initialization-status');
  },

  /**
   * Initialize system with first admin user
   * @param {object} adminData - Admin account data
   * @param {string} adminData.username - Admin username
   * @param {string} adminData.password - Admin password
   * @param {string} adminData.password_confirm - Password confirmation
   * @returns {Promise<{success: boolean, message: string, user_id: string}>}
   */
  initializeAdmin: async (adminData) => {
    return await api.post('/api/v1/system/initialize-admin', adminData);
  },
};

/**
 * Questions API methods (if/when implemented)
 */
export const questionsApi = {
  /**
   * Get all questions
   * @param {object} [params] - Query parameters
   * @returns {Promise<Array>} List of questions
   */
  getQuestions: async (params = {}) => {
    return await api.get('/api/v1/questions', { params });
  },

  /**
   * Get question by ID
   * @param {string} questionId - Question ID
   * @returns {Promise<object>} Question data
   */
  getQuestion: async (questionId) => {
    return await api.get(`/api/v1/questions/${questionId}`);
  },

  /**
   * Create new question
   * @param {object} questionData - Question data
   * @returns {Promise<object>} Created question
   */
  createQuestion: async (questionData) => {
    return await api.post('/api/v1/questions', questionData);
  },

  /**
   * Answer a question
   * @param {string} questionId - Question ID
   * @param {string} answer - Answer text
   * @returns {Promise<object>} Updated question
   */
  answerQuestion: async (questionId, answer) => {
    return await api.patch(`/api/v1/questions/${questionId}/answer`, { answer });
  },

  /**
   * Upvote a question
   * @param {string} questionId - Question ID
   * @returns {Promise<object>} Updated question
   */
  upvoteQuestion: async (questionId) => {
    return await api.post(`/api/v1/questions/${questionId}/upvote`);
  },
};

// Export all API services
export default {
  auth: authApi,
  chat: chatApi,
  users: usersApi,
  products: productsApi,
  knowledge: knowledgeApi,
  questions: questionsApi,
  system: systemApi,
  company: companyApi,
};
